// Admin-only Benutzerverwaltung.
// Liste mit Anlegen, Rolle ändern, Passwort setzen, Löschen.

import { useEffect, useState } from 'react';

import { api, getUser } from '../api.js';
import Modal from './Modal.jsx';

const ROLE_LABEL = { admin: 'Admin', user: 'Standard' };

export default function Users() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [pwUser, setPwUser] = useState(null);

    const currentUsername = getUser();

    async function reload() {
        setLoading(true);
        setError('');
        try {
            setUsers(await api.listUsers());
        } catch (err) {
            setError(err.message || 'Laden fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        reload();
    }, []);

    async function handleRoleChange(user, role) {
        if (user.role === role) return;
        try {
            await api.updateUser(user.id, { role });
            reload();
        } catch (err) {
            alert(err.message || 'Rollenwechsel fehlgeschlagen');
        }
    }

    async function handleDelete(user) {
        if (!confirm(`User „${user.username}“ wirklich löschen?`)) return;
        try {
            await api.deleteUser(user.id);
            reload();
        } catch (err) {
            alert(err.message || 'Löschen fehlgeschlagen');
        }
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h1>Benutzerverwaltung</h1>
                <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
                    Neuer Benutzer
                </button>
            </div>

            {error && <div className="login__error" style={{ marginBottom: 12 }}>{error}</div>}

            {loading ? (
                <p>Lädt…</p>
            ) : (
                <div className="table-wrap"><table>
                    <thead>
                        <tr>
                            <th>Benutzername</th>
                            <th>Rolle</th>
                            <th>Angelegt</th>
                            <th style={{ width: 1 }}>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id}>
                                <td>
                                    {u.username}
                                    {u.username === currentUsername && (
                                        <span style={{ marginLeft: 8, color: 'var(--color-text-muted)' }}>(du)</span>
                                    )}
                                </td>
                                <td>
                                    <select
                                        value={u.role}
                                        onChange={(e) => handleRoleChange(u, e.target.value)}
                                    >
                                        <option value="admin">{ROLE_LABEL.admin}</option>
                                        <option value="user">{ROLE_LABEL.user}</option>
                                    </select>
                                </td>
                                <td>{u.created_at}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => setPwUser(u)}
                                    >
                                        Passwort setzen
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn--danger"
                                        style={{ marginLeft: 6 }}
                                        onClick={() => handleDelete(u)}
                                        disabled={u.username === currentUsername}
                                    >
                                        Löschen
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table></div>
            )}

            {showCreate && (
                <CreateUserModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => {
                        setShowCreate(false);
                        reload();
                    }}
                />
            )}
            {pwUser && (
                <SetPasswordModal
                    user={pwUser}
                    onClose={() => setPwUser(null)}
                />
            )}
        </div>
    );
}

function CreateUserModal({ onClose, onCreated }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('user');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            await api.createUser({ username: username.trim(), password, role });
            onCreated();
        } catch (err) {
            setError(err.message || 'Anlegen fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal title="Neuen Benutzer anlegen" onClose={onClose}>
            <form onSubmit={submit}>
                {error && <div className="login__error" style={{ marginBottom: 12 }}>{error}</div>}
                <div className="field">
                    <label>Benutzername</label>
                    <input value={username} onChange={(e) => setUsername(e.target.value)} required />
                </div>
                <div className="field">
                    <label>Passwort (mind. 8 Zeichen)</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        minLength={8}
                        required
                    />
                </div>
                <div className="field">
                    <label>Rolle</label>
                    <select value={role} onChange={(e) => setRole(e.target.value)}>
                        <option value="user">Standard</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button type="button" className="btn" onClick={onClose}>Abbrechen</button>
                    <button type="submit" className="btn btn--primary" disabled={saving}>
                        {saving ? 'Speichert…' : 'Anlegen'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function SetPasswordModal({ user, onClose }) {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            await api.setUserPassword(user.id, password);
            setDone(true);
        } catch (err) {
            setError(err.message || 'Passwort setzen fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal title={`Passwort setzen – ${user.username}`} onClose={onClose}>
            {done ? (
                <div>
                    <p>Passwort wurde aktualisiert.</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" className="btn btn--primary" onClick={onClose}>Schließen</button>
                    </div>
                </div>
            ) : (
                <form onSubmit={submit}>
                    {error && <div className="login__error" style={{ marginBottom: 12 }}>{error}</div>}
                    <div className="field">
                        <label>Neues Passwort (mind. 8 Zeichen)</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            minLength={8}
                            required
                            autoFocus
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button type="button" className="btn" onClick={onClose}>Abbrechen</button>
                        <button type="submit" className="btn btn--primary" disabled={saving}>
                            {saving ? 'Speichert…' : 'Speichern'}
                        </button>
                    </div>
                </form>
            )}
        </Modal>
    );
}
